import { createRef, useEffect, useState, type ReactNode } from "react";
import styles from "./switcher-sheet.module.css";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { useTranslation } from "@coworker/locales";
import FixaSearch from "../../../../shared/wrappers/FixaSearch/FixaSearch";
import { FixaListView } from "../../../../shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "../../../../shared/wrappers/FixaListView/FixaListViewItem";
import { FixaSkeleton } from "../../../../shared/wrappers/FixaSkeleton/FixaSkeleton";

interface SwitcherSheetProps<T> {
  titleKey: string;
  placeholderKey: string;
  items: T[];
  selectedItem?: T;
  getItemLabel: (item: T) => string;
  getItemKey: (item: T) => string;
  onSelect: (item: T) => void;
  isDisabled?: (items: T[]) => void;
  loading: boolean;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  infoNode?: ReactNode;
}

export function SwitcherSheet<T>({
  titleKey,
  placeholderKey,
  items,
  selectedItem,
  getItemLabel,
  getItemKey,
  onSelect,
  isDisabled,
  loading,
  isOpen,
  setIsOpen,
  infoNode,
}: SwitcherSheetProps<T>) {
  const { t } = useTranslation();
  const searchInputRef = createRef<HTMLInputElement>();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (isDisabled) isDisabled(items);
  }, [items, isDisabled]);

  const filteredItems = items
    .filter((item) =>
      getItemLabel(item).toLowerCase().includes(query.trim().toLowerCase())
    )
    .sort((a, b) => getItemLabel(a).localeCompare(getItemLabel(b)));

  return (
    <FixaModal visible={isOpen} handleCloseBtn={() => setIsOpen(false)}>
      <FixaSheets
        labelledById="home-profile-switcher-header"
        alignment="right"
        preserveAlignment={false}
        size="small"
        fullSize
        header={
          <>
            <FixaModalHeader
              id="home-profile-switcher-header"
              title={t(titleKey)}
              className={styles["header"] as string}
              floating={false}
              closeBtnClick={() => setIsOpen(false)}
            />
            {loading ? (
              <div className={styles["search-wrapper"]}>
                <FixaSkeleton width="100%" height="50px" />
              </div>
            ) : (
              <div className={styles["search-wrapper"]}>
                <FixaSearch
                  id={`search-${titleKey}`}
                  ref={searchInputRef}
                  placeholder={t(placeholderKey)}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onClear={() => setQuery("")}
                />
              </div>
            )}
          </>
        }
        footer={null}
      >
        <FixaModalBody className={styles["modal-body"] as string}>
          {infoNode && !loading && (
            <div className={styles["info-node-wrapper"]}>{infoNode}</div>
          )}
          {loading ? (
            <div className={styles["skeleton-wrapper"]}>
              <FixaSkeleton width="100%" height="24px" />
              <FixaSkeleton width="100%" height="24px" />
              <FixaSkeleton width="100%" height="24px" />
              <FixaSkeleton width="100%" height="24px" />
              <FixaSkeleton width="100%" height="24px" />
            </div>
          ) : (
            <FixaListView
              id={`list-${titleKey}`}
              size="small"
              showDivider={false}
            >
              {filteredItems.map((item) => (
                <FixaListViewItem
                  key={getItemKey(item)}
                  id={getItemKey(item)}
                  control="radiobutton"
                  onChange={() => {
                    onSelect(item);
                    setIsOpen(false);
                  }}
                  inset
                  title={getItemLabel(item)}
                  size="small"
                  controlProps={{
                    checked: getItemKey(item) === getItemKey(selectedItem!),
                    value: getItemLabel(item),
                  }}
                />
              ))}
            </FixaListView>
          )}
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
}
